import React from "react";
import { CrudPage } from "@/components/CrudPage";
import { StatusBadge } from "@/components/StatusBadge";
import { INVOICE_STATUS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATUS_OPTS = Object.entries(INVOICE_STATUS).map(([value, v]) => ({ value, label: v.label }));

const columns = [
  { key: "year_month", label: "請求年月", render: (r) => <span className="font-medium">{r.year_month}</span> },
  { key: "property_id", label: "物件", render: (r, h) => h.nameOf("properties", r.property_id) },
  { key: "tenant_id", label: "入居者", render: (r, h) => h.nameOf("tenants", r.tenant_id) },
  { key: "amount", label: "請求額", render: (r, h) => h.yen(r.amount) },
  { key: "paid_amount", label: "入金額", render: (r, h) => h.yen(r.paid_amount) },
  { key: "balance", label: "未収額", render: (r, h) => <span className={r.balance > 0 ? "text-red-600" : ""}>{h.yen(r.balance)}</span> },
  { key: "due_date", label: "支払期限", render: (r, h) => h.dateJP(r.due_date) },
  { key: "status", label: "状態", render: (r) => <StatusBadge value={r.status} map={INVOICE_STATUS} /> },
];

const fields = [
  { name: "contract_id", label: "契約", type: "select", lookup: "contracts" },
  { name: "property_id", label: "物件", type: "select", lookup: "properties", required: true },
  { name: "room_id", label: "部屋", type: "select", lookup: "rooms" },
  { name: "tenant_id", label: "入居者", type: "select", lookup: "tenants" },
  { name: "year_month", label: "請求年月 (YYYY-MM)", required: true },
  { name: "due_date", label: "支払期限", type: "date" },
  { name: "rent", label: "賃料", type: "currency" },
  { name: "common_fee", label: "共益費", type: "currency" },
  { name: "management_fee", label: "管理費", type: "currency" },
  { name: "parking_fee", label: "駐車場", type: "currency" },
  { name: "water_fee", label: "水道代", type: "currency" },
  { name: "other_fee", label: "その他費用", type: "currency" },
  { name: "status", label: "ステータス", type: "select", options: STATUS_OPTS, default: "issued" },
];

export default function Invoices() {
  const generate = async (reload) => {
    try {
      const { data } = await api.post("/billing/invoices/generate", {});
      toast.success(`${data.year_month} の請求を${data.created}件生成しました`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "生成に失敗しました");
    }
  };

  return (
    <CrudPage
      title="家賃請求管理"
      subtitle="契約内容から家賃請求を管理"
      endpoint="/billing/invoices"
      resource="invoices"
      columns={columns}
      fields={fields}
      testid="invoices"
      addLabel="請求を追加"
      searchKeys={["year_month", "status"]}
      extraActions={(reload) => (
        <Button variant="outline" onClick={() => generate(reload)} data-testid="generate-invoices">
          <RefreshCw className="w-4 h-4 mr-1.5" />今月分を自動生成
        </Button>
      )}
    />
  );
}
